import { lazy, Suspense, ComponentType } from 'react';
import { Loader2 } from 'lucide-react';

// Loading fallback - shown while lazy component loads
export const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
  </div>
);

// HOC to wrap lazy components with Suspense
export function withSuspense<P extends object>(
  LazyComponent: React.LazyExoticComponent<ComponentType<P>>
) {
  return function SuspenseWrapper(props: P) {
    return (
      <Suspense fallback={<PageLoader />}>
        <LazyComponent {...props} />
      </Suspense>
    );
  };
}

/**
 * Wrapper for React.lazy that auto-reloads on chunk load errors.
 * Uses sessionStorage to prevent infinite reload loops.
 * Industry standard pattern - used by Vercel, GitHub, Slack.
 */
export function lazyRetry<T extends ComponentType<Record<string, unknown>>>(
  componentImport: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(() => new Promise((resolve, reject) => {
    const hasRefreshed = JSON.parse(
      sessionStorage.getItem('retry-lazy-refreshed') || 'false'
    );
    componentImport()
      .then((component) => {
        sessionStorage.setItem('retry-lazy-refreshed', 'false');
        resolve(component);
      })
      .catch((error) => {
        if (!hasRefreshed) {
          sessionStorage.setItem('retry-lazy-refreshed', 'true');
          window.location.reload();
          return;
        }
        reject(error);
      });
  }));
}

// ============ PLATFORM ADMIN PAGES ============
export const LazyPlatformAdmin = lazyRetry(() => import('./pages/PlatformAdmin'));
export const LazyVoiceLab = lazyRetry(() => import('./pages/VoiceLab')); // Platform-admin Labs → Voice Lab (Demo)
export const LazyPlatformAdminUsers = lazyRetry(() => import('./pages/PlatformAdminUsers'));
export const LazyPlatformAdminFormLinks = lazyRetry(() => import('./pages/PlatformAdminFormLinks'));
export const LazyPlatformAdminTenants = lazyRetry(() => import('./pages/PlatformAdminTenants'));
export const LazyPlatformAdminSpaceWizard = lazyRetry(() => import('./pages/PlatformAdminSpaceWizard'));
export const LazyPlatformAdminBilling = lazyRetry(() => import('./pages/PlatformAdminBilling'));
export const LazyPlatformAdminLogging = lazyRetry(() => import('./pages/PlatformAdminLogging'));
export const LazyPlatformAdminAPIHealth = lazyRetry(() => import('./pages/PlatformAdminAPIHealth'));
export const LazyPlatformAdminRehearsalEnvironment = lazyRetry(() => import('./pages/PlatformAdminRehearsalEnvironment'));
export const LazyPlatformAdminTesting = lazyRetry(() => import('./pages/PlatformAdminTesting'));
export const LazyPlatformAdminAIManagement = lazyRetry(() => import('./pages/PlatformAdminAIManagement'));
export const LazyPlatformAdminTestModels = lazyRetry(() => import('./pages/PlatformAdminTestModels'));
export const LazyPlatformAdminAITemplates = lazyRetry(() => import('./pages/PlatformAdminAITemplates'));
export const LazyPlatformAdminTemplates = lazyRetry(() => import('./pages/PlatformAdminTemplates'));
export const LazyPlatformAdminConversationTesting = lazyRetry(() => import('./pages/PlatformAdminConversationTesting'));
export const LazyPlatformAdminConversationSimulator = lazyRetry(() => import('./pages/PlatformAdminConversationSimulator'));
export const LazyPlatformAdminTenantUsers = lazyRetry(() => import('./pages/PlatformAdminTenantUsers'));
export const LazyPlatformAdminTenantSettings = lazyRetry(() => import('./pages/PlatformAdminTenantSettings'));
export const LazyPlatformAdminTenantRuntimeConfig = lazyRetry(() => import('./pages/PlatformAdminTenantRuntimeConfig'));
export const LazyPlatformAdminTenantAIAgents = lazyRetry(() => import('./pages/PlatformAdminTenantAIAgents'));
export const LazyPlatformAdminTenantWorkflowBuilder = lazyRetry(() => import('./pages/PlatformAdminTenantWorkflowBuilder'));
export const LazyPlatformAdminTenantProducts = lazyRetry(() => import('./pages/PlatformAdminTenantProducts'));
export const LazyPlatformAdminTenantWhatsApp = lazyRetry(() => import('./pages/PlatformAdminTenantWhatsApp'));
export const LazyPlatformAdminTenantAnalytics = lazyRetry(() => import('./pages/PlatformAdminTenantAnalytics'));
export const LazyPlatformAdminTenantLogging = lazyRetry(() => import('./pages/PlatformAdminTenantLogging'));
export const LazyPlatformAdminTenantConversations = lazyRetry(() => import('./pages/PlatformAdminTenantConversations'));
export const LazyWhatsAppAdminPlatform = lazyRetry(() => import('./pages/WhatsAppAdminPlatform'));
export const LazySystemHealth = lazyRetry(() => import('./pages/SystemHealth'));

// ============ HEAVY FEATURE PAGES ============
export const LazyTenantWhatsAppFlows = lazyRetry(() => import('./pages/TenantWhatsAppFlows'));
export const LazyTenantWorkflowBuilder = lazyRetry(() => import('./pages/TenantWorkflowBuilder'));
export const LazyTenantCustomExtensions = lazyRetry(() => import('./pages/TenantCustomExtensions'));
export const LazyTenantConversationSimulator = lazyRetry(() => import('./pages/TenantConversationSimulator'));
export const LazyConversationsAnalytics = lazyRetry(() => import('./pages/ConversationsAnalytics'));
export const LazyConversationsV2 = lazyRetry(() => import('./pages/ConversationsV2'));
export const LazyTenantEmailTemplates = lazyRetry(() => import('./pages/tenant-admin/TenantEmailTemplates'));
export const LazyTenantAdminCalendarBooking = lazyRetry(() => import('./pages/TenantAdminCalendarBooking'));
export const LazyTenantAdminCalendarConfig = lazyRetry(() => import('./pages/TenantAdminCalendarConfig'));
export const LazyAppStudioBuilder = lazyRetry(() => import('./modules/app-studio/pages/AppStudioBuilder'));
export const LazyTenantAppStudio = lazyRetry(() => import('./pages/TenantAppStudio'));
export const LazyTenantAppStudioApp = lazyRetry(() => import('./pages/TenantAppStudioApp'));
export const LazyTenantAppStudioPageViewer = lazyRetry(() => import('./pages/TenantAppStudioPageViewer'));

// ============ ADMIN FEATURE PAGES ============
export const LazyTenantAIAgents = lazyRetry(() => import('./pages/TenantAIAgents'));
export const LazyTenantAIWorkflows = lazyRetry(() => import('./pages/TenantAIWorkflows'));
export const LazyTenantAISettings = lazyRetry(() => import('./pages/TenantAISettings'));
export const LazyTenantContextStores = lazyRetry(() => import('./pages/TenantContextStores'));
export const LazyEscalationSettings = lazyRetry(() => import('./pages/EscalationSettings'));
export const LazyPaymentSettings = lazyRetry(() => import('./pages/PaymentSettings'));
export const LazyTenantAdminCredentials = lazyRetry(() => import('./pages/TenantAdminCredentials'));
export const LazySettings = lazyRetry(() => import('./pages/Settings'));
